import React, { createContext, useContext, useState, useEffect } from "react";

export type Route = {
  path: string;
  component: React.ComponentType<any>;
  title?: string;
  description?: string;
};

interface RouterContextType {
  currentPath: string;
  navigate: (path: string) => void;
  params: Record<string, string>;
}

const RouterContext = createContext<RouterContextType | undefined>(undefined);

export function useRouter() {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error("useRouter must be used within a RouterProvider");
  }
  return context;
}

interface RouterProviderProps {
  routes: Route[];
  children: React.ReactNode;
}

export function RouterProvider({ routes, children }: RouterProviderProps) {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [params, setParams] = useState<Record<string, string>>({});

  // Parse path parameters
  const parseParams = (routePath: string, actualPath: string): Record<string, string> => {
    const routeSegments = routePath.split("/").filter(Boolean);
    const pathSegments = actualPath.split("/").filter(Boolean);

    const params: Record<string, string> = {};

    routeSegments.forEach((segment, index) => {
      if (segment.startsWith(":")) {
        const paramName = segment.slice(1);
        params[paramName] = pathSegments[index] || "";
      }
    });

    return params;
  };

  // Find matching route
  const findRoute = (path: string): Route | null => {
    const exactMatch = routes.find(route => route.path === path);
    if (exactMatch) return exactMatch;

    // Try to match routes with parameters
    for (const route of routes) {
      if (route.path.includes(":")) {
        const routeSegments = route.path.split("/").filter(Boolean);
        const pathSegments = path.split("/").filter(Boolean);

        if (routeSegments.length === pathSegments.length) {
          const isMatch = routeSegments.every((segment, index) =>
            segment.startsWith(":") || segment === pathSegments[index]
          );

          if (isMatch) {
            const params = parseParams(route.path, path);
            setParams(params);
            return route;
          }
        }
      }
    }

    return null;
  };

  // Handle navigation
  const navigate = (path: string) => {
    window.history.pushState({}, "", path);
    setCurrentPath(path);

    const route = findRoute(path);
    if (route?.title) {
      document.title = route.title;
    }
    if (route?.description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', route.description);
      }
    }
  };

  // Listen to browser navigation
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Update current path when it changes
  useEffect(() => {
    const route = findRoute(currentPath);
    if (route) {
      setParams(parseParams(route.path, currentPath));
      if (route.title) document.title = route.title;
    }
  }, [currentPath, routes]);

  const contextValue: RouterContextType = {
    currentPath,
    navigate,
    params,
  };

  return (
    <RouterContext.Provider value={contextValue}>
      {children}
    </RouterContext.Provider>
  );
}

interface RouterProps {
  routes: Route[];
  fallback?: React.ComponentType;
}

export function Router({ routes, fallback: FallbackComponent }: RouterProps) {
  const { currentPath } = useRouter();

  const route = routes.find(r => {
    if (r.path === currentPath) return true;

    // Check for parameterized routes
    if (r.path.includes(":")) {
      const routeSegments = r.path.split("/").filter(Boolean);
      const pathSegments = currentPath.split("/").filter(Boolean);

      if (routeSegments.length === pathSegments.length) {
        return routeSegments.every((segment, index) =>
          segment.startsWith(":") || segment === pathSegments[index]
        );
      }
    }

    return false;
  });

  if (route) {
    const Component = route.component;
    return <Component />;
  }

  if (FallbackComponent) {
    return <FallbackComponent />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">404</h1>
        <p className="text-xl text-muted-foreground">Page not found</p>
      </div>
    </div>
  );
}

// Navigation component for easier routing
interface NavLinkProps {
  to: string;
  className?: string;
  activeClassName?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

export function NavLink({
  to,
  className = "",
  activeClassName = "active",
  children,
  onClick
}: NavLinkProps) {
  const { currentPath, navigate } = useRouter();

  const isActive = currentPath === to ||
    (currentPath.startsWith(to) && to !== "/");

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
    onClick?.();
  };

  return (
    <a
      href={to}
      className={`${className} ${isActive ? activeClassName : ""}`}
      onClick={handleClick}
    >
      {children}
    </a>
  );
}